/**
 * CompleteJobConfirmDialog Component
 * Confirmation dialog for marking a job as production complete
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';

const DEFAULT_BULLETS = [
  'Change job status to "Completed"',
  'Remove job from all Production Line tabs',
];

export interface CompleteJobConfirmDialogProps {
  isOpen: boolean;
  /** Shown in default lead when `leadContent` is not set. */
  jobNumber?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  title?: string;
  /** Overrides the default lead paragraph (job number highlight). */
  leadContent?: React.ReactNode;
  sectionHeading?: string;
  bullets?: string[];
  cancelLabel?: string;
  confirmLabel?: string;
  confirmingLabel?: string;
  /** Tailwind width classes for the dialog panel (e.g. max-w-lg for bulk). */
  panelClassName?: string;
  /** Rendered after the bullet list, above the action buttons (e.g. optional form fields). */
  extraContent?: React.ReactNode;
  /** Red confirm button (e.g. delete / irreversible actions). */
  destructive?: boolean;
  /** When true, clicking the dimmed backdrop calls onCancel (when not loading). */
  closeOnBackdropClick?: boolean;
}

export const CompleteJobConfirmDialog: React.FC<CompleteJobConfirmDialogProps> = ({
  isOpen,
  jobNumber = '',
  onConfirm,
  onCancel,
  isLoading = false,
  title = 'Complete Production?',
  leadContent,
  sectionHeading = 'This will:',
  bullets = DEFAULT_BULLETS,
  cancelLabel = 'Cancel',
  confirmLabel = 'Yes, Complete Production',
  confirmingLabel = 'Completing...',
  panelClassName = 'max-w-md',
  extraContent,
  destructive = false,
  closeOnBackdropClick = false,
}) => {
  if (!isOpen) return null;

  const lead =
    leadContent ??
    (
      <>
        Are you sure you want to mark job{' '}
        <strong className="font-bold text-gray-900">{jobNumber}</strong> as production complete?
      </>
    );

  const confirmBtnClass = destructive
    ? 'px-4 py-2 bg-red-600 text-white rounded-md text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors'
    : 'px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role={closeOnBackdropClick ? 'presentation' : undefined}
      onClick={
        closeOnBackdropClick
          ? () => {
              if (!isLoading) onCancel();
            }
          : undefined
      }
    >
      <div
        className={`bg-white rounded-lg p-6 ${panelClassName} w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-600 text-sm">{lead}</p>
            <p className="text-gray-600 text-sm mt-2">{sectionHeading}</p>
            <ul className="list-disc list-inside text-gray-600 text-sm mt-1 ml-2">
              {bullets.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            {extraContent ? (
              <div className="mt-4 border-t border-gray-200 pt-4">{extraContent}</div>
            ) : null}
          </div>
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={confirmBtnClass}
          >
            {isLoading ? confirmingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
